"use client";

import { useEffect, useState } from "react";
import { Menu, Search, User, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CartIcon } from "@/components/shop/CartIcon";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetClose, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-10">
        <div className="h-3 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-3 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-3 w-14 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-10">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-foreground text-[13px] font-default font-medium uppercase tracking-[0.14em] hover:text-primary transition-colors duration-300 relative group bg-transparent hover:bg-transparent focus:bg-transparent px-0 py-0">
                <span className="relative">
                  {item.label}
                  <span className="absolute left-0 -bottom-1 h-px w-0 bg-primary group-hover:w-full transition-all duration-300 ease-out" />
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 px-6">
        <div className="h-8 w-40 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-32 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-44 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-8 px-8 mt-8">
      {menuItems.map((item, i) => <SheetClose asChild key={item.id}>
          <Link data-index={i} to={item.href || "#"} onClick={onClose} className="text-foreground font-default text-3xl font-light tracking-tight hover:text-primary transition-colors duration-300">
            {item.label}
          </Link>
        </SheetClose>)}
    </nav>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="13346" id="the-main-header" className={`sticky top-0 left-0 right-0 z-50 w-full transition-all duration-[400ms] ease-out bg-background text-foreground ${scrolled ? "bg-background/90 backdrop-blur-md py-4 border-b border-border" : "bg-background py-5 md:py-6"}`}>
      <div className="w-full px-6 md:px-20">
        <div className="hidden lg:grid grid-cols-12 items-center gap-8">
          <div className="col-span-5 flex items-center">
            <MenuProvider menu_id="23" className="header-menu">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          <div className="col-span-2 flex justify-center items-center">
            <Link to="/" className="inline-flex items-center">
              <WvcLogo className="h-[22px] w-auto" />
            </Link>
          </div>

          <div className="col-span-5 flex items-center justify-end gap-5">
            <Button type="button" variant="ghost" size="icon" onClick={() => setSearchOpen(v => !v)} aria-label="Search" className="h-auto w-auto p-2 hover:bg-transparent text-foreground hover:text-primary transition-colors duration-200">
              <Search className="size-6" strokeWidth={1.25} />
            </Button>
            <Button asChild variant="ghost" size="icon" aria-label="Account" className="h-auto w-auto p-2 hover:bg-transparent text-foreground hover:text-primary transition-colors duration-200">
              <Link to="#">
                <User className="size-6" strokeWidth={1.25} />
              </Link>
            </Button>
            <CartIcon variant="ghost" size="icon" iconSize="size-6" className="h-auto w-auto p-2 hover:bg-transparent text-foreground hover:text-primary transition-colors duration-200 [&_svg]:!size-6" />
          </div>
        </div>

        <div className="flex lg:hidden items-center justify-between">
          <Link to="/" className="inline-flex items-center">
            <WvcLogo className="h-5 w-auto" />
          </Link>

          <div className="flex items-center gap-2">
            <Button type="button" variant="ghost" size="icon" onClick={() => setSearchOpen(v => !v)} aria-label="Search" className="h-auto w-auto p-2 hover:bg-transparent text-foreground">
              <Search className="size-5" strokeWidth={1.25} />
            </Button>
            <CartIcon variant="ghost" size="icon" iconSize="size-5" className="h-auto w-auto p-2 hover:bg-transparent text-foreground [&_svg]:!size-5" />

            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button type="button" variant="ghost" size="icon" aria-label="Open menu" className="h-auto w-auto p-2 hover:bg-transparent text-foreground">
                  <Menu className="size-6" strokeWidth={1.25} />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:max-w-full bg-background border-0 p-0 [&>button]:hidden">
                <div className="flex items-center justify-between px-6 py-5 md:py-6">
                  <Link to="/" onClick={() => setMobileOpen(false)} className="inline-flex items-center">
                    <WvcLogo className="h-5 w-auto" />
                  </Link>
                  <SheetClose asChild>
                    <Button type="button" variant="ghost" size="icon" aria-label="Close menu" className="h-auto w-auto p-2 hover:bg-transparent text-foreground">
                      <X className="size-6" strokeWidth={1.25} />
                    </Button>
                  </SheetClose>
                </div>

                <div className="animate-in fade-in duration-700">
                  <MenuProvider menu_id="23" className="mobile-menu">
                    <MobileMenuConsumer onClose={() => setMobileOpen(false)} />
                  </MenuProvider>

                  <div className="mt-12 px-8 flex flex-col gap-6">
                    <SheetClose asChild>
                      <Link to="#" className="text-foreground font-default text-[13px] font-medium uppercase tracking-[0.18em]">
                        My Account
                      </Link>
                    </SheetClose>
                    <SheetClose asChild>
                      <Link to="/CartPage" className="text-foreground font-default text-[13px] font-medium uppercase tracking-[0.18em]">
                        View Cart
                      </Link>
                    </SheetClose>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {searchOpen && <div className="absolute left-0 right-0 top-full bg-background/95 backdrop-blur-md animate-in fade-in duration-[400ms] border-b border-border">
            <div className="mx-auto max-w-[640px] px-6 py-10 flex items-center gap-4">
              <Search className="size-5 text-muted-foreground" strokeWidth={1.25} />
              <input type="text" autoFocus placeholder="Search our artisanal flavors..." className="flex-1 bg-transparent border-0 border-b-2 border-foreground focus:border-primary focus:outline-none py-3 text-base font-default text-foreground placeholder:text-muted-foreground transition-colors duration-[250ms]" />
              <Button type="button" variant="ghost" size="icon" onClick={() => setSearchOpen(false)} aria-label="Close search" className="h-auto w-auto p-2 hover:bg-transparent text-foreground">
                <X className="size-5" strokeWidth={1.25} />
              </Button>
            </div>
          </div>}
      </div>
    </section>;
}